import { createContext, useContext, useState, type ReactNode } from 'react'

export interface OpenSarahOptions {
  episodeId?: string
  resultId?: string
  // Sent automatically as the first message once Sarah opens — used by
  // contextual entry points like "Give me a phishing scenario" so the
  // learner doesn't have to type it themselves.
  initialPrompt?: string
}

interface SarahUIContextValue {
  isOpen: boolean
  options: OpenSarahOptions
  openSarah: (options?: OpenSarahOptions) => void
  closeSarah: () => void
}

const SarahUIContext = createContext<SarahUIContextValue | null>(null)

// Global open/close state for the Sarah panel (Document 11 §P3). Kept
// separate from the panel's own conversation state so any entry point
// anywhere in the app — Dashboard, Episode Details, Quiz Results — can call
// openSarah() without needing to know anything about how Sarah renders.
export function SarahUIProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<OpenSarahOptions>({})

  const openSarah = (opts: OpenSarahOptions = {}) => {
    setOptions(opts)
    setIsOpen(true)
  }
  const closeSarah = () => setIsOpen(false)

  return (
    <SarahUIContext.Provider value={{ isOpen, options, openSarah, closeSarah }}>{children}</SarahUIContext.Provider>
  )
}

export function useSarahUI(): SarahUIContextValue {
  const ctx = useContext(SarahUIContext)
  if (!ctx) throw new Error('useSarahUI must be used within SarahUIProvider')
  return ctx
}
